'use client';

import { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { format, isWithinInterval } from 'date-fns';
import { MdArrowOutward } from 'react-icons/md';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@components/common/Dialog';
import { InterviewLists } from './InterviewLists';
import { InterviewCalendar } from './InterviewCalendar';
import { parseDate } from '@utils/dateUtils';
import useCandidatePipelineStore from '@stores/candidatePipelineStore';
import { InterviewScheduleSkeleton } from '@components/loaders/SkeletonLoaders';

function InterviewSchedule() {
    const {
        interviewScheduledData,
        fetchInterviewScheduledData,
        clearStore,
        loadingInterviewScheduledData,
    } = useCandidatePipelineStore();

    useEffect(() => {
        fetchInterviewScheduledData();
        return clearStore;
    }, [fetchInterviewScheduledData, clearStore]);

    const [dateRange, setDateRange] = useState();
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);

    const filteredCandidates = useMemo(() => {
        return interviewScheduledData.filter(candidate => {
            const matchesSearch =
                (candidate?.candidate_name || '').toLowerCase().includes(search.toLowerCase()) ||
                candidate.job_title.toLowerCase().includes(search.toLowerCase());

            const candidateDate = parseDate(candidate.interview_scheduled_at);

            if (!candidateDate) return false;

            const matchesDateRange =
                !dateRange?.from ||
                !dateRange?.to ||
                isWithinInterval(candidateDate, {
                    start: new Date(dateRange.from.setHours(0, 0, 0, 0)),
                    end: new Date(dateRange.to.setHours(23, 59, 59, 999)),
                });

            return matchesSearch && matchesDateRange;
        });
    }, [interviewScheduledData, search, dateRange]);

    const interviewDates = useMemo(() => {
        const dates = new Map();
        interviewScheduledData.forEach(candidate => {
            const candidateDate = parseDate(candidate.interview_scheduled_at);
            if (candidateDate) {
                const date = format(candidateDate, 'yyyy-MM-dd');
                if (!dates.has(date)) dates.set(date, []);
                dates.get(date).push(candidate);
            }
        });
        return dates;
    }, [interviewScheduledData]);

    const clearFilters = () => {
        setDateRange(undefined);
        setSearch('');
    };

    return (
        <Dialog className="z-[99999]" open={open} onOpenChange={() => setOpen(!open)}>
            <div className="w-full col-span-2  p-4 rounded-[10px] bg-white mb-4">
                <div className="flex items-center justify-between mb-4">
                    <div className="text-lg font-bold">Interview Schedule:</div>
                    <DialogTrigger>
                        <div className="flex items-center justify-center gap-2 text-sm text-primary-blue hover:text-blue-800 font-semibold cursor-pointer">
                            Show all <MdArrowOutward />
                        </div>
                    </DialogTrigger>
                </div>

                <DialogContent fullscreen={true} className="bg-white h-[calc(100vh-100px)]">
                    <DialogHeader>
                        <DialogTitle>Interview Schedule:</DialogTitle>
                    </DialogHeader>
                    <div className="h-full flex lg:flex-row flex-col items-start gap-2 relative box-border transition-all duration-300">
                        <div className="lg:w-7/12 w-full">
                            <InterviewLists
                                filteredCandidates={filteredCandidates}
                                search={search}
                                setSearch={setSearch}
                                dateRange={dateRange}
                                clearFilters={clearFilters}
                            />
                        </div>
                        <div className="lg:w-5/12 w-full">
                            <InterviewCalendar
                                dateRange={dateRange}
                                setDateRange={setDateRange}
                                interviewDates={interviewDates}
                            />
                        </div>
                    </div>
                </DialogContent>

                {loadingInterviewScheduledData && <InterviewScheduleSkeleton />}

                {!loadingInterviewScheduledData && (
                    <div className="h-full flex lg:flex-row flex-col items-start gap-2 relative box-border transition-all duration-300">
                        <div className="lg:w-7/12 w-full">
                            <InterviewLists
                                filteredCandidates={filteredCandidates}
                                search={search}
                                setSearch={setSearch}
                                dateRange={dateRange}
                                clearFilters={clearFilters}
                            />
                        </div>
                        <div className="lg:w-5/12 w-full">
                            <InterviewCalendar
                                dateRange={dateRange}
                                setDateRange={setDateRange}
                                interviewDates={interviewDates}
                            />
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    );
}

InterviewSchedule.propTypes = {
    interviewScheduledData: PropTypes.array,
};

export default InterviewSchedule;
